// =====================================================================
// src/lib/v2/wallPosts.js
//
// Phase 2.5 / C10 — V2 Merchant Wall data layer.
//
// Mirrors V1's group posts pattern (db.js createPost/updatePost/etc.)
// but for the V2 collection /businesses/{bizId}/posts/{postId}.
//
// Helpers exported here:
//   createWallPost({ bizId, authorId, type, text, photoFiles })
//     — uploads photos lazily (pre-generated postId so storage paths
//       are stable before the Firestore doc is created), then writes
//       the post doc with the resolved photo URLs.
//   updateWallPost({ bizId, postId, ownerUid, updates })
//     — supports text/type/photo edits. Photo edits handle add+remove
//       by diffing the new photos array against the current one.
//   deleteWallPost({ bizId, postId })
//     — deletes the post doc + all 3 photo files. Sparks/comments
//       subcollections are left as orphan docs (no recursive delete
//       in the client SDK; cost is negligible since nothing queries
//       them after the parent is gone).
//   listWallPosts({ bizId, onSnap, limit = 50 })
//     — subscribes to onSnapshot, returns unsubscribe.
//
// Engagement helpers (sparkPost, addComment, etc.) live here too but
// are stubbed until C10d wires the customer-side UI.
// =====================================================================

import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
  query,
  orderBy,
  limit as firestoreLimit,
  increment,
  runTransaction,
  getDocs,
  getDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  uploadWallPostImage,
  deleteWallPostImage,
  deleteAllWallPostImages,
} from './storagePhotos';

// =====================================================================
// CONSTANTS
// =====================================================================

export const POST_TYPES = [
  { id: 'offer', label: 'Offer', emoji: '🏷️' },
  { id: 'new_product', label: 'New product', emoji: '🆕' },
  { id: 'announcement', label: 'Announcement', emoji: '📢' },
  { id: 'general', label: 'General', emoji: '💬' },
];

export const MAX_POST_TEXT = 500;
export const MAX_POST_PHOTOS = 3;

// =====================================================================
// CREATE
// =====================================================================

/**
 * Create a new wall post with optional photos.
 *
 * Two-phase write: photos upload BEFORE the Firestore doc exists, using
 * a pre-generated postId so storage paths line up. The Firestore rule
 * for posts/{postId} doesn't require the doc to exist for uploads —
 * just owner check via storage rule's isBusinessOwner. After all photos
 * upload, setDoc creates the post atomically with the resolved URLs.
 *
 * Photo failures are non-fatal — successfully-uploaded photos are kept,
 * failed ones are dropped from the array. Caller can decide whether to
 * surface a warning.
 *
 * @param {object} args
 * @param {string} args.bizId
 * @param {string} args.authorId  — owner uid
 * @param {string} args.type      — one of POST_TYPES ids
 * @param {string} args.text      — up to MAX_POST_TEXT
 * @param {File[]} [args.photoFiles] — up to MAX_POST_PHOTOS
 * @returns {Promise<{ postId, photosUploaded, photosFailed }>}
 */
export async function createWallPost({ bizId, authorId, type, text, photoFiles = [] }) {
  if (!bizId) throw new Error('bizId required');
  if (!authorId) throw new Error('authorId required');
  if (!type) throw new Error('type required');
  if (typeof text !== 'string') throw new Error('text required');
  if (text.length > MAX_POST_TEXT) throw new Error(`Post text exceeds ${MAX_POST_TEXT} chars`);
  if (!text.trim() && photoFiles.length === 0) {
    throw new Error('Post must have text or at least one photo');
  }
  if (photoFiles.length > MAX_POST_PHOTOS) {
    throw new Error(`Maximum ${MAX_POST_PHOTOS} photos per post`);
  }

  // Pre-generate the postId so storage paths can use it before Firestore write.
  const postRef = doc(collection(db, 'businesses', bizId, 'posts'));
  const postId = postRef.id;

  // Upload photos in parallel. Each succeeds or fails independently.
  // We resolve at .all() with both arrays so partial failures don't
  // tank the whole post.
  const uploadResults = await Promise.allSettled(
    photoFiles.map((file, i) => uploadWallPostImage(bizId, postId, file, authorId, i))
  );

  const photos = [];
  const failedIndices = [];
  uploadResults.forEach((r, i) => {
    if (r.status === 'fulfilled') {
      photos.push(r.value);
    } else {
      failedIndices.push(i);
      console.warn(`Wall post photo ${i} failed:`, r.reason);
    }
  });

  // Write the post doc. Counters start at 0 (rule-enforced). createdAt
  // uses serverTimestamp so client clock skew can't backdate posts.
  await setDoc(postRef, {
    businessId: bizId,
    authorId,
    type,
    text: text.trim(),
    photos,
    sparkCount: 0,
    commentCount: 0,
    createdAt: serverTimestamp(),
    editedAt: null,
  });

  return {
    postId,
    photosUploaded: photos.length,
    photosFailed: failedIndices.length,
  };
}

// =====================================================================
// UPDATE
// =====================================================================

/**
 * Edit an existing wall post. Supports text, type, and photo changes.
 *
 * Photo edits work by diffing `nextPhotos` (the URLs the caller wants
 * the post to end up with, in slot order 0/1/2) against the current
 * `currentPhotos` (slot order preserved). Slots that go from URL → null
 * have their photo deleted. Slots that have a fresh File in
 * `photoFiles[i]` are uploaded.
 *
 * The caller is responsible for handing us a coherent picture:
 *   currentPhotos: ['urlA', 'urlB', null]  (the post's current state)
 *   nextPhotos:    ['urlA',  null,  null]  (after edit)
 *   photoFiles:    [null,    null,  File]  (new file at slot 2)
 *
 * @param {object} args
 * @param {string} args.bizId
 * @param {string} args.postId
 * @param {string} args.ownerUid
 * @param {object} args.updates
 * @param {string} [args.updates.type]
 * @param {string} [args.updates.text]
 * @param {(string|null)[]} [args.updates.currentPhotos] — existing array
 * @param {(string|null)[]} [args.updates.nextPhotos]    — desired array
 * @param {(File|null)[]}   [args.updates.photoFiles]    — new files per slot
 */
export async function updateWallPost({ bizId, postId, ownerUid, updates }) {
  if (!bizId) throw new Error('bizId required');
  if (!postId) throw new Error('postId required');
  if (!ownerUid) throw new Error('ownerUid required');
  if (!updates) throw new Error('updates required');

  const docUpdate = { editedAt: serverTimestamp() };

  if (typeof updates.text === 'string') {
    if (updates.text.length > MAX_POST_TEXT) throw new Error(`Text exceeds ${MAX_POST_TEXT} chars`);
    docUpdate.text = updates.text.trim();
  }
  if (typeof updates.type === 'string') {
    if (!POST_TYPES.find(t => t.id === updates.type)) throw new Error('Invalid post type');
    docUpdate.type = updates.type;
  }

  // Photo diff handling. Caller passes:
  //   currentPhotos[i] — what's there now (URL or null)
  //   nextPhotos[i]    — what should be there after (URL kept, null = remove, replaced = new)
  //   photoFiles[i]    — new File to upload at slot i (if nextPhotos[i] is meant to be from a new upload)
  if (updates.currentPhotos && updates.nextPhotos) {
    const current = updates.currentPhotos;
    const next = updates.nextPhotos;
    const files = updates.photoFiles || [];

    if (next.length > MAX_POST_PHOTOS) {
      throw new Error(`Maximum ${MAX_POST_PHOTOS} photos per post`);
    }

    // Step 1: delete photos that are leaving (current had URL, next is null)
    const deletePromises = [];
    for (let i = 0; i < MAX_POST_PHOTOS; i++) {
      const had = current[i];
      const wants = next[i];
      if (had && !wants) {
        deletePromises.push(deleteWallPostImage(bizId, postId, i));
      }
    }
    await Promise.all(deletePromises);

    // Step 2: upload new photos for slots where photoFiles[i] is set.
    // After upload, next[i] gets replaced with the new URL.
    const resolvedNext = [...next];
    const uploadPromises = [];
    for (let i = 0; i < MAX_POST_PHOTOS; i++) {
      if (files[i]) {
        uploadPromises.push(
          uploadWallPostImage(bizId, postId, files[i], ownerUid, i)
            .then(url => { resolvedNext[i] = url; })
            .catch(err => {
              console.warn(`Wall post photo edit upload ${i} failed:`, err);
              // Keep original next[i] which was probably null; the slot ends up empty.
              resolvedNext[i] = null;
            })
        );
      }
    }
    await Promise.all(uploadPromises);

    // Compact: remove null/empty slots so the stored array has no gaps.
    docUpdate.photos = resolvedNext.filter(p => p);
  }

  await updateDoc(doc(db, 'businesses', bizId, 'posts', postId), docUpdate);
}

// =====================================================================
// DELETE
// =====================================================================

/**
 * Delete a wall post and its photos.
 *
 * Sparks/comments subcollection docs are left orphaned. They're never
 * queried again after the parent is gone and cost ~$0 to leave behind.
 * A future Cloud Function could prune them on a schedule if needed.
 *
 * @param {object} args
 * @param {string} args.bizId
 * @param {string} args.postId
 */
export async function deleteWallPost({ bizId, postId }) {
  if (!bizId) throw new Error('bizId required');
  if (!postId) throw new Error('postId required');
  // Photos first (Firestore rules require owner; if doc deletion fails
  // for some reason, photos are already gone — acceptable trade-off
  // since the photo files are useless without the post doc).
  await deleteAllWallPostImages(bizId, postId);
  await deleteDoc(doc(db, 'businesses', bizId, 'posts', postId));
}

// =====================================================================
// READ — REAL-TIME LISTENER
// =====================================================================

/**
 * Subscribe to a business's wall posts in real time, ordered newest first.
 *
 * @param {object} args
 * @param {string}   args.bizId
 * @param {function} args.onSnap     — receives an array of post objects
 *                                     `{ id, ...data }`
 * @param {function} [args.onError]
 * @param {number}   [args.limit=50]
 * @returns {function} unsubscribe
 */
export function listWallPosts({ bizId, onSnap, onError, limit = 50 }) {
  if (!bizId) throw new Error('bizId required');
  if (typeof onSnap !== 'function') throw new Error('onSnap callback required');

  const q = query(
    collection(db, 'businesses', bizId, 'posts'),
    orderBy('createdAt', 'desc'),
    firestoreLimit(limit)
  );

  return onSnapshot(
    q,
    (snap) => {
      const posts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      onSnap(posts);
    },
    (err) => {
      console.error('listWallPosts error:', err);
      if (typeof onError === 'function') onError(err);
    }
  );
}

/**
 * Fetch ONE most-recent wall post for a business. One-shot read, no
 * listener. Used by the Home Connect List to surface a teaser pill per
 * business tile ("🏷️ 20% off weekend", etc.) without subscribing to
 * every connected business's wall in real time.
 *
 * Returns null if the business has no posts (404 / empty collection).
 * Throws only on permission errors so the caller can decide to log /
 * fall back.
 *
 * @param {string} bizId
 * @returns {Promise<object|null>}  the post object `{ id, ...data }` or null
 */
export async function getLatestWallPost(bizId) {
  if (!bizId) return null;
  try {
    const q = query(
      collection(db, 'businesses', bizId, 'posts'),
      orderBy('createdAt', 'desc'),
      firestoreLimit(1)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  } catch (err) {
    // Permission/network failures shouldn't kill the home screen.
    console.warn('[getLatestWallPost] failed for', bizId, err?.code || err?.message);
    return null;
  }
}

/**
 * Check which posts (from a given list) the current user has sparked.
 *
 * Used by Wall sub-tab + detail screen to render filled-Zap state on
 * cards. Reads sparks/{userId} docs in parallel — one read per post in
 * the visible list. For typical wall sizes (≤50) this is cheap.
 *
 * @param {object} args
 * @param {string} args.bizId
 * @param {string} args.userId   — viewer uid
 * @param {string[]} args.postIds
 * @returns {Promise<Set<string>>}  postIds the user has sparked
 */
export async function getSparkedPostIds({ bizId, userId, postIds }) {
  if (!bizId || !userId || !Array.isArray(postIds) || postIds.length === 0) {
    return new Set();
  }
  const results = await Promise.allSettled(
    postIds.map(postId =>
      getDoc(doc(db, 'businesses', bizId, 'posts', postId, 'sparks', userId))
    )
  );
  const sparked = new Set();
  results.forEach((r, i) => {
    if (r.status === 'fulfilled' && r.value.exists()) {
      sparked.add(postIds[i]);
    }
  });
  return sparked;
}

// =====================================================================
// ENGAGEMENT (sparks + comments) — wired in C10d
// =====================================================================
// Exposed here so C10b's UI can show buttons that no-op gracefully until
// C10d ships. Each helper writes BOTH the per-user doc AND increments
// the counter on the post in a single transaction — the firestore rule
// allows both writes when paired this way.

/**
 * Toggle a spark on a post. If the user has already sparked, removes
 * their spark and decrements the counter. Otherwise creates the spark
 * doc and increments. Atomic via runTransaction.
 *
 * @param {object} args
 * @param {string} args.bizId
 * @param {string} args.postId
 * @param {string} args.userId
 * @returns {Promise<{ sparked: boolean, newCount: number }>}
 */
export async function toggleSpark({ bizId, postId, userId }) {
  if (!bizId || !postId || !userId) throw new Error('bizId, postId, userId required');
  const postRef = doc(db, 'businesses', bizId, 'posts', postId);
  const sparkRef = doc(db, 'businesses', bizId, 'posts', postId, 'sparks', userId);

  return await runTransaction(db, async (tx) => {
    const [postSnap, sparkSnap] = await Promise.all([tx.get(postRef), tx.get(sparkRef)]);
    if (!postSnap.exists()) throw new Error('Post not found');

    const alreadySparked = sparkSnap.exists();
    const currentCount = postSnap.data().sparkCount || 0;

    if (alreadySparked) {
      tx.delete(sparkRef);
      tx.update(postRef, { sparkCount: increment(-1) });
      return { sparked: false, newCount: Math.max(0, currentCount - 1) };
    } else {
      tx.set(sparkRef, { sparkedAt: serverTimestamp() });
      tx.update(postRef, { sparkCount: increment(1) });
      return { sparked: true, newCount: currentCount + 1 };
    }
  });
}

/**
 * Add a comment to a post. Returns the new comment id.
 *
 * @param {object} args
 * @param {string} args.bizId
 * @param {string} args.postId
 * @param {string} args.authorId
 * @param {string} args.text
 * @returns {Promise<string>} commentId
 */
export async function addComment({ bizId, postId, authorId, text }) {
  if (!bizId || !postId || !authorId) throw new Error('bizId, postId, authorId required');
  if (typeof text !== 'string' || !text.trim()) throw new Error('text required');
  const trimmed = text.trim().slice(0, 1000); // light cap; V1 doesn't enforce here either

  const postRef = doc(db, 'businesses', bizId, 'posts', postId);
  const commentRef = doc(collection(db, 'businesses', bizId, 'posts', postId, 'comments'));

  await runTransaction(db, async (tx) => {
    const postSnap = await tx.get(postRef);
    if (!postSnap.exists()) throw new Error('Post not found');
    tx.set(commentRef, {
      authorId,
      text: trimmed,
      createdAt: serverTimestamp(),
      editedAt: null,
    });
    tx.update(postRef, { commentCount: increment(1) });
  });

  return commentRef.id;
}

/**
 * Subscribe to a post's comments, oldest first.
 */
export function listComments({ bizId, postId, onSnap, onError, limit = 100 }) {
  if (!bizId || !postId) throw new Error('bizId and postId required');
  const q = query(
    collection(db, 'businesses', bizId, 'posts', postId, 'comments'),
    orderBy('createdAt', 'asc'),
    firestoreLimit(limit)
  );
  return onSnapshot(
    q,
    (snap) => onSnap(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error('listComments error:', err);
      if (typeof onError === 'function') onError(err);
    }
  );
}

/**
 * Delete a comment. Counter decrements transactionally.
 */
export async function deleteComment({ bizId, postId, commentId }) {
  if (!bizId || !postId || !commentId) throw new Error('bizId, postId, commentId required');
  const postRef = doc(db, 'businesses', bizId, 'posts', postId);
  const commentRef = doc(db, 'businesses', bizId, 'posts', postId, 'comments', commentId);
  await runTransaction(db, async (tx) => {
    const [postSnap, commentSnap] = await Promise.all([tx.get(postRef), tx.get(commentRef)]);
    if (!commentSnap.exists()) return;
    tx.delete(commentRef);
    if (postSnap.exists()) {
      tx.update(postRef, { commentCount: increment(-1) });
    }
  });
}
